import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { useEffect } from 'react';
import { FieldErrors } from 'react-hook-form';
import styled from 'styled-components';

import ClassificationPanelContent from 'dashboard/components/ClassificationsConfig/ClassificationPanelContent';
import { ClassificationPanelForm } from 'dashboard/components/ClassificationsConfig/util';
import {
  Classification,
  ClassificationMethod,
  DefinitionType,
  ToggleOption,
} from 'dashboard/models/document';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Button, { IconButton } from 'design-system/components/Button';
import Stack from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

const PANEL_FOOTER_HEIGHT = 64;
const ERROR_REQUIRED = { type: 'required', message: 'Required' };

const Form = styled.form`
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]};
  padding-bottom: 0;
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const classificationToFormData = (
  classification: Classification | null,
): Partial<ClassificationPanelForm> => {
  if (classification == null) {
    return {};
  }
  return {
    label: classification.label,
    key: classification.key,
    description: classification.description || '',
  };
};

const formDataToClassification = (data: ClassificationPanelForm): Classification => {
  return {
    key: data.key,
    'definition-type': DefinitionType.Custom,
    'classification-method': ClassificationMethod.Ai,
    label: data.label,
    description: data.description,
    'toggle-option': ToggleOption.Enabled,
  };
};

const validateFormData = (
  data: ClassificationPanelForm,
  vtmplVersion: VerificationTemplateVersionDocument,
  selected: number | null,
) => {
  const errors: FieldErrors<ClassificationPanelForm> = {};

  const { classifications } = vtmplVersion.attributes;

  const enabledClassifications = classifications.filter(
    (classification, index) =>
      classification['toggle-option'] === ToggleOption.Enabled && selected !== index,
  );
  const usedClassificationKeys = enabledClassifications.map((classification) => classification.key);

  if (data.label == null || data.label === '') {
    errors.label = ERROR_REQUIRED;
  }
  if (usedClassificationKeys.includes(data.key)) {
    errors.key = {
      type: 'conflict',
      message: 'A classification with this key already exists. Please choose a different key.',
    };
  }

  return Object.keys(errors).length === 0
    ? { values: data, errors: {} }
    : { values: {}, errors: errors };
};

interface ClassificationPanelProps {
  vtmplVersion: VerificationTemplateVersionDocument;
  selected: number | null;
  onClose: () => void;
  onAdd: (classification: Classification) => void;
  onUpdate: (classification: Classification) => void;
  onDelete: () => void;
}

const ClassificationPanel = ({
  vtmplVersion,
  selected,
  onClose,
  onAdd,
  onUpdate,
  onDelete,
}: ClassificationPanelProps) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [onClose]);

  const { classifications } = vtmplVersion.attributes;

  const isNew = selected == null;
  const classification = !isNew ? classifications[selected] : null;

  const formReturn = useFormSecurely<ClassificationPanelForm>({
    defaultValues: classificationToFormData(classification),
    resolver: (data) => validateFormData(data, vtmplVersion, selected),
    mode: 'onChange',
  });
  const { handleSubmit } = formReturn;

  const handleSubmitForm = handleSubmit((data) => {
    if (isNew) {
      onAdd(formDataToClassification(data));
    } else {
      onUpdate(formDataToClassification(data));
    }
    onClose();
  });

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  return (
    <Form id='classification-panel__form' onSubmit={handleSubmitForm}>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            {isNew ? 'Create' : 'Update'} document type
          </Paragraph>
        </TitleContainer>
        <ClassificationPanelContent formReturn={formReturn} />
      </BodyContainer>
      <FooterContainer>
        <Stack direction='horizontal' style={{ flexGrow: 1 }}>
          <Button
            id={`classification-panel__${isNew ? 'add' : 'update'}-button`}
            type='submit'
            width='auto'
            onClick={handleSubmitForm}
          >
            {isNew ? 'Add' : 'Update'}
          </Button>
          <Button
            id='classification-panel__close-button'
            width='auto'
            variant='basic'
            onClick={onClose}
          >
            Cancel
          </Button>
        </Stack>
        <IconButton
          id='classification-panel__delete-button'
          onClick={handleDelete}
          disabled={isNew}
        >
          <TrashIcon />
        </IconButton>
      </FooterContainer>
    </Form>
  );
};

export default ClassificationPanel;
